import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User, LoginCredentials, RegisterData, VerifyOtpData, Catalog } from '@/types';
import api from '@/services/api';

export const useAuthStore = defineStore('auth', () => {
  // ─── State ───
  const token = ref<string | null>(localStorage.getItem('token'));
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  // Email pendiente de verificación OTP
  const pendingEmail = ref<string>(localStorage.getItem('pending_email') || '');
  // Flag para saber si ya terminó la inicialización
  const initialized = ref(false);
  // Incrementa en cada login/verify exitoso para disparar flujos post-autenticación
  const authSessionId = ref(0);

  // ─── Getters ───
  const isAuthenticated = computed(() => !!token.value && !!user.value && user.value.verified);

  function hasPrivilege(code: string) {
    return user.value?.privileges?.includes(code) ?? false;
  }

  function hasAnyPrivilege(codes: string[]) {
    return codes.some((code) => hasPrivilege(code));
  }

  // ─── Helpers ───
  function setSession(jwt: string, u: User) {
    token.value = jwt;
    user.value = u;
    localStorage.setItem('token', jwt);
    localStorage.removeItem('pending_email');
    pendingEmail.value = '';
    api.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
    authSessionId.value += 1;
  }

  function clearSession() {
    token.value = null;
    user.value = null;
    localStorage.removeItem('token');
    localStorage.removeItem('pending_email');
    pendingEmail.value = '';
    delete api.defaults.headers.common['Authorization'];
  }

  // ─── Actions ───

  /** Registrar cuenta nueva → retorna email y teléfono pendiente de OTP */
  async function signup(data: RegisterData): Promise<{ success: boolean; needsOtp: boolean; email?: string; phone?: string }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/auth/register', data);

      if (response.data?.needs_otp) {
        pendingEmail.value = data.email;
        localStorage.setItem('pending_email', data.email);
        return { success: true, needsOtp: true, email: data.email, phone: response.data.phone };
      }

      return { success: true, needsOtp: false };
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al crear la cuenta';
      return { success: false, needsOtp: false };
    } finally {
      loading.value = false;
    }
  }

  /** Login → puede retornar needsOtp si cuenta no verificada (403) */
  async function login(credentials: LoginCredentials): Promise<{
    success: boolean;
    needsOtp?: boolean;
    phone?: string;
    previousLoginAt?: string | null;
  }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/auth/login', credentials);

      const { token: jwt, user: u, previous_login_at: previousLoginAt } = response.data;
      setSession(jwt, u);
      return { success: true, previousLoginAt: previousLoginAt ?? null };
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data;

      if (!err.response) {
        error.value = 'No se pudo conectar con el servidor. Verifica que la API esté activa.';
        return { success: false };
      }

      // 403 = puede ser cuenta no verificada (needs_otp) o desactivada
      if (status === 403) {
        if (data?.needs_otp) {
          pendingEmail.value = data.email || credentials.email;
          localStorage.setItem('pending_email', pendingEmail.value);
          return { success: false, needsOtp: true, phone: data?.phone || '' };
        }
        // Cuenta desactivada u otro bloqueo
        error.value = data?.error || 'Acceso denegado';
        return { success: false };
      }

      error.value = data?.error === 'Invalid credentials'
        ? 'Credenciales inválidas'
        : (data?.error || 'Error al iniciar sesión');
      return { success: false };
    } finally {
      loading.value = false;
    }
  }

  /** Verificar OTP → activa cuenta y genera session */
  async function verifyOtp(data: VerifyOtpData): Promise<{ success: boolean; previousLoginAt?: string | null }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post('/auth/verify', data);

      const { token: jwt, user: u, previous_login_at: previousLoginAt } = response.data;
      setSession(jwt, u);
      return { success: true, previousLoginAt: previousLoginAt ?? null };
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al verificar el código';
      return { success: false };
    } finally {
      loading.value = false;
    }
  }

  /** Reenviar OTP */
  async function resendOtp(email: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      await api.post('/auth/resend-otp', { email });
      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al reenviar el código';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /** Subir avatar de perfil */
  async function uploadAvatar(file: File): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await api.post('/users/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Actualizar user con la nueva URL
      if (user.value) {
        user.value.profile_picture = response.data.profile_picture;
      }

      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al subir la imagen';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /** Subir firma digital */
  async function uploadSignature(
    file: File,
    options?: {
      signatureReference?: string;
      signatureIdType?: string;
      signatureIdNumber?: string;
    },
  ): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append('signature', file);
      if (options?.signatureReference !== undefined) {
        formData.append('signature_reference', options.signatureReference);
      }
      if (options?.signatureIdType) {
        formData.append('signature_id_type', options.signatureIdType);
      }
      if (options?.signatureIdNumber) {
        formData.append('signature_id_number', options.signatureIdNumber);
      }

      const response = await api.post('/users/me/signature', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (user.value) {
        user.value.signature_image = response.data.signature_image;
        if (response.data.signature_reference !== undefined) {
          user.value.signature_reference = response.data.signature_reference;
        }
        if (response.data.signature_id_type !== undefined) {
          user.value.signature_id_type = response.data.signature_id_type;
        }
        if (response.data.signature_id_number !== undefined) {
          user.value.signature_id_number = response.data.signature_id_number;
        }
      }

      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al subir la firma';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /** Actualizar datos de la firma (referencia e identificación) */
  async function updateSignatureReference(payload: {
    signature_reference: string;
    signature_id_type?: string;
    signature_id_number?: string;
  }): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.put('/users/me/signature/reference', payload);

      if (user.value) {
        user.value.signature_reference = response.data.signature_reference;
        user.value.signature_id_type = response.data.signature_id_type || '';
        user.value.signature_id_number = response.data.signature_id_number || '';
      }

      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al guardar la referencia';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /** Eliminar firma digital */
  async function deleteSignature(): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      await api.delete('/users/me/signature');

      if (user.value) {
        user.value.signature_image = undefined;
      }

      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al eliminar la firma';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /** Actualizar especialidades del usuario autenticado */
  async function updateMySpecialties(catalogIds: number[]): Promise<Catalog[] | null> {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.put('/users/me/specialties', { catalog_ids: catalogIds });

      if (user.value) {
        user.value.specialties = response.data;
      }

      return response.data as Catalog[];
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al guardar especialidades';
      return null;
    } finally {
      loading.value = false;
    }
  }

  /** Obtener especialidades del usuario autenticado */
  async function fetchMySpecialties(): Promise<Catalog[]> {
    try {
      const response = await api.get('/users/me/specialties');
      const items = response.data as Catalog[];

      if (user.value) {
        user.value.specialties = items;
      }

      return items;
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al cargar especialidades';
      return user.value?.specialties || [];
    }
  }

  /** Logout */
  async function logout() {
    clearSession();
    error.value = null;
  }

  /** Actualizar perfil del usuario autenticado */
  async function updateProfile(data: Partial<User>): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      if (!user.value) {
        error.value = 'No hay sesión activa';
        return false;
      }

      const response = await api.put(`/users/${user.value.id}`, data);

      // Actualizar solo las propiedades del user existente
      Object.assign(user.value, response.data);

      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Error al actualizar el perfil';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /** Refresca usuario y privilegios actuales desde el backend */
  async function refreshUser(): Promise<void> {
    if (!token.value) return;

    try {
      const response = await api.get('/auth/me');
      user.value = response.data as User;
    } catch {
      // Silencioso: el guard usará el estado en memoria
    }
  }

  /** Inicializar auth al cargar la app: restaura token y recupera user desde backend */
  async function initializeAuth() {
    const storedToken = localStorage.getItem('token');
    const storedEmail = localStorage.getItem('pending_email');

    if (storedToken) {
      token.value = storedToken;
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

      try {
        const response = await api.get('/auth/me');
        user.value = response.data as User;
      } catch (err: any) {
        // Solo borrar token si el backend dijo 401 (token inválido/expirado)
        // Si es timeout u otro error de red, mantener el token y dejar que reintente
        if (err.response?.status === 401) {
          clearSession();
        }
        // Cualquier otro error (timeout, 500, etc.): mantener token, user queda null
        // El router guard detectará isAuthenticated=false y redirigirá
      }
    }

    if (storedEmail) {
      pendingEmail.value = storedEmail;
    }

    initialized.value = true;
  }

  return {
    token,
    user,
    loading,
    error,
    pendingEmail,
    initialized,
    authSessionId,
    isAuthenticated,
    hasPrivilege,
    hasAnyPrivilege,
    signup,
    login,
    verifyOtp,
    resendOtp,
    uploadAvatar,
    uploadSignature,
    updateSignatureReference,
    deleteSignature,
    updateProfile,
    updateMySpecialties,
    fetchMySpecialties,
    logout,
    clearSession,
    refreshUser,
    initializeAuth,
  };
});
